import React, {useEffect, useState} from 'react';
import {WeChatMessageTemplateDto} from "../../../../../modules/wechat/dto/WeChatMessageTemplateDto";
import {useIntl} from "react-intl";
import {HttpStatus} from "../../../../../modules/common/enums/HttpStatus";
import {WeChatMessageTemplateApi} from "../../../../../modules/wechat/api/WeChatMessageTemplateApi";

import {Dialog, DialogActions, DialogContent, DialogTitle,} from "@mui/material";
import FormFieldText from "../../../../../components/FormFieldText";
import CancelButton from "../../../../../components/buttons/CancelButton";
import SaveButton from "../../../../../components/buttons/SaveButton";
import {MessageUtils} from "../../../../../utils/MessageUtils";
import {WeChatApplicationDto} from "../../../../../modules/wechat/dto/WeChatApplicationDto";
import FormFieldSelect from "../../../../../components/FormFieldSelect";

export type WeChatMessageTemplateDetailDialogProps = {
    open: boolean;
    wechatApplicationList: Array<WeChatApplicationDto>;
    wechatMessageTemplate: WeChatMessageTemplateDto;
    onClose: () => void;
    onOk: () => void;
};

const MessageTemplateDetailDialog: React.FC<WeChatMessageTemplateDetailDialogProps> = (props) => {
    const intl = useIntl();
    const title = props.wechatMessageTemplate.id ? 'WECHAT_MESSAGE_TEMPLATE_UPDATE' : 'WECHAT_MESSAGE_TEMPLATE_CREATE';

    const [innerWeChatMessageTemplate, setInnerWeChatMessageTemplate] = useState<WeChatMessageTemplateDto>(props.wechatMessageTemplate);

    const [messageMap, setMessageMap] = useState(new Map<string, string>());

    useEffect(() => {
        if (props.open) {
            setInnerWeChatMessageTemplate({...props.wechatMessageTemplate});
        }
    }, [props.open]);

    const _handleChange = (name: string, value: any) => {
        setInnerWeChatMessageTemplate(previousInnerWeChatMessageTemplate => ({...previousInnerWeChatMessageTemplate, [name]: value}));
    };

    const _handleOk = async () => {
        if (_validate()) {
            if (props.wechatMessageTemplate.id) {
                const responseResult = await WeChatMessageTemplateApi.updateMessageTemplate(innerWeChatMessageTemplate);
                if (responseResult.status === HttpStatus.SUCCESS) {
                    MessageUtils.success(intl.formatMessage({id: 'WECHAT_MESSAGE_TEMPLATE_UPDATE_SUCCESS'}));
                    props.onClose();
                    props.onOk();
                }
            } else {
                const responseResult = await WeChatMessageTemplateApi.createMessageTemplate(innerWeChatMessageTemplate);
                if (responseResult.status === HttpStatus.SUCCESS) {
                    MessageUtils.success(intl.formatMessage({id: 'WECHAT_MESSAGE_TEMPLATE_CREATE_SUCCESS'}));
                    props.onClose();
                    props.onOk();
                }
            }
        }
    };

    const _handleCancel = () => {
        setMessageMap(new Map<string, string>());
        props.onClose();
    };

    const _validate = () => {
        const newMessageMap = new Map<string, string>();

        if (innerWeChatMessageTemplate.applicationId === '') {
            newMessageMap.set('applicationId', intl.formatMessage({id: 'WECHAT_MESSAGE_TEMPLATE_APPLICATION_ID_REQUIRED'}));
        }
        if (innerWeChatMessageTemplate.code === '') {
            newMessageMap.set('code', intl.formatMessage({id: 'WECHAT_MESSAGE_TEMPLATE_CODE_REQUIRED'}));
        }
        if (innerWeChatMessageTemplate.name === undefined || innerWeChatMessageTemplate.name === '') {
            newMessageMap.set('name', intl.formatMessage({id: 'WECHAT_MESSAGE_TEMPLATE_NAME_REQUIRED'}));
        }
        if (innerWeChatMessageTemplate.templateId === '') {
            newMessageMap.set('templateId', intl.formatMessage({id: 'WECHAT_MESSAGE_TEMPLATE_TEMPLATE_ID_REQUIRED'}));
        }
        if (innerWeChatMessageTemplate.templateTitle === '') {
            newMessageMap.set('templateTitle', intl.formatMessage({id: 'WECHAT_MESSAGE_TEMPLATE_TEMPLATE_TITLE_REQUIRED'}));
        }

        setMessageMap(newMessageMap);
        return newMessageMap.size <= 0;
    };

    return (
        <Dialog open={props.open} onClose={_handleCancel} fullWidth={true} maxWidth={'md'}>
            <DialogTitle>{intl.formatMessage({id: title})}</DialogTitle>
            <DialogContent>
                <FormFieldSelect label={intl.formatMessage({id: "WECHAT_MESSAGE_TEMPLATE_APPLICATION_ID"})}
                                 required={true} errorText={messageMap.get("applicationId")}
                                 value={innerWeChatMessageTemplate.applicationId}
                                 onChange={(value) => {
                                     _handleChange("applicationId", value)
                                 }}
                                 items={props.wechatApplicationList.map((item) => {
                                         return {label: item.name, value: item.id}
                                     }
                                 )}/>
                <FormFieldText label={intl.formatMessage({id: "WECHAT_MESSAGE_TEMPLATE_CODE"})}
                               required={true} errorText={messageMap.get("code")}
                               value={innerWeChatMessageTemplate.code}
                               onChange={(value) => {
                                   _handleChange("code", value)
                               }}/>
                <FormFieldText label={intl.formatMessage({id: "WECHAT_MESSAGE_TEMPLATE_NAME"})}
                               required={true} errorText={messageMap.get("name")}
                               value={innerWeChatMessageTemplate.name}
                               onChange={(value) => {
                                   _handleChange("name", value)
                               }}/>
                <FormFieldText label={intl.formatMessage({id: "WECHAT_MESSAGE_TEMPLATE_TEMPLATE_ID"})}
                               required={true} errorText={messageMap.get("templateId")}
                               value={innerWeChatMessageTemplate.templateId}
                               onChange={(value) => {
                                   _handleChange("templateId", value)
                               }}/>
                <FormFieldText label={intl.formatMessage({id: "WECHAT_MESSAGE_TEMPLATE_TEMPLATE_TITLE"})}
                               required={true} errorText={messageMap.get("templateTitle")}
                               value={innerWeChatMessageTemplate.templateTitle}
                               onChange={(value) => {
                                   _handleChange("templateTitle", value)
                               }}/>
                <FormFieldText label={intl.formatMessage({id: "WECHAT_MESSAGE_TEMPLATE_TEMPLATE_KEYWORD1"})}
                               value={innerWeChatMessageTemplate.templateKeyword1}
                               onChange={(value) => {
                                   _handleChange("templateKeyword1", value)
                               }}/>
                <FormFieldText label={intl.formatMessage({id: "WECHAT_MESSAGE_TEMPLATE_TEMPLATE_KEYWORD2"})}
                               value={innerWeChatMessageTemplate.templateKeyword2}
                               onChange={(value) => {
                                   _handleChange("templateKeyword2", value)
                               }}/>
                <FormFieldText label={intl.formatMessage({id: "WECHAT_MESSAGE_TEMPLATE_TEMPLATE_KEYWORD3"})}
                               value={innerWeChatMessageTemplate.templateKeyword3}
                               onChange={(value) => {
                                   _handleChange("templateKeyword3", value)
                               }}/>
                <FormFieldText label={intl.formatMessage({id: "WECHAT_MESSAGE_TEMPLATE_TEMPLATE_KEYWORD4"})}
                               value={innerWeChatMessageTemplate.templateKeyword4}
                               onChange={(value) => {
                                   _handleChange("templateKeyword4", value)
                               }}/>
                <FormFieldText label={intl.formatMessage({id: "WECHAT_MESSAGE_TEMPLATE_TEMPLATE_KEYWORD5"})}
                               value={innerWeChatMessageTemplate.templateKeyword5}
                               onChange={(value) => {
                                   _handleChange("templateKeyword5", value)
                               }}/>
                <FormFieldText label={intl.formatMessage({id: "WECHAT_MESSAGE_TEMPLATE_PAGE_URL"})}
                               value={innerWeChatMessageTemplate.pageUrl}
                               onChange={(value) => {
                                   _handleChange("pageUrl", value)
                               }}/>
            </DialogContent>
            <DialogActions>
                <CancelButton onClick={_handleCancel}/>
                <SaveButton onClick={_handleOk}/>
            </DialogActions>
        </Dialog>
    );
};

export default MessageTemplateDetailDialog;
